import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ratesService from "../../../service/rates.service";

interface Rate {
  id: string;
  authCode: string;
  modifier: string;
  perType: string;
  currency: string;
  rateBilled: number;
  rateAgreed: number;
  employeeId: string;
  employeeRate: number;
  insuranceProviderId: string;
  insuranceProvider?: {
    id: string;
    insuranceProviderName: string;
  };
  effectiveDate: string;
  expirationDate: string;
}

interface RatesState {
  rates: Rate[];
  totalRecords: number;
  loading: boolean;
  error: string | null;
}

const initialState: RatesState = {
  rates: [],
  totalRecords: 0,
  loading: false,
  error: null,
};

// Get rates with filtering and pagination
export const getRates = createAsyncThunk(
  "ratesSlice/getRates",
  async (params: Record<string, any> = {}) => {
    try {
      const response = await ratesService.getRates(params);
      return {
        result: response.result || [],
        count: response.count || 0,
      };
    } catch (error: any) {
      throw error?.message || "Failed to fetch rates";
    }
  }
);

// Create a new rate
export const createRate = createAsyncThunk(
  "ratesSlice/createRate",
  async (payload: any, { dispatch }) => {
    try {
      const response = await ratesService.createRate(payload);
      dispatch(
        getRates({
          active: true,
          skip: 0,
          take: 10,
        })
      );
      return response;
    } catch (error: any) {
      throw error?.message || "Failed to create rate";
    }
  }
);

// Update an existing rate
export const updateRate = createAsyncThunk(
  "ratesSlice/updateRate",
  async ({ id, payload }: { id: string; payload: any }, { dispatch }) => {
    try {
      const response = await ratesService.updateRate(id, payload);
      dispatch(
        getRates({
          active: true,
          skip: 0,
          take: 10,
        })
      );
      return response;
    } catch (error: any) {
      throw error?.message || "Failed to update rate";
    }
  }
);

// export const deleteRate = createAsyncThunk(
// 	"ratesSlice/deleteRate",
// 	async (id: string, { dispatch }) => {
// 		try {
// 			const response = await ratesService.deleteRate(id);
// 			dispatch(
// 				getRates({
// 					active: true,
// 					skip: 0,
// 					take: 10,
// 				}),
// 			);
// 			return id;
// 		} catch (error: any) {
// 			throw error?.message || "Failed to delete rate";
// 		}
// 	},
// );

const ratesSlice = createSlice({
  name: "ratesSlice",
  initialState,
  reducers: {
    clearRatesError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Handle getRates
    builder.addCase(getRates.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getRates.fulfilled, (state: any, action) => {
      state.loading = false;
      state.rates = action.payload.result;
      state.totalRecords = action.payload.count;
    });
    builder.addCase(getRates.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch rates";
    });

    // Handle createRate
    builder.addCase(createRate.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createRate.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(createRate.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to create rate";
    });

    // Handle updateRate
    builder.addCase(updateRate.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateRate.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(updateRate.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to update rate";
    });
  },
});

export const { clearRatesError } = ratesSlice.actions;
export default ratesSlice;
